import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, Upload, Spin, message } from 'antd';
import { BASE_URL } from '../api';
import OrdersTable from './OrdersTable';

function compressImage(file) {
  return new Promise((resolve) => {
    const img = new window.Image();
    const reader = new FileReader();
    reader.onload = (e) => {
      img.onload = () => {
        let { width, height } = img;
        if (width > 900 || height > 900) {
          const scale = Math.min(900 / width, 900 / height);
          width = width * scale;
          height = height * scale;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const base64 = canvas.toDataURL('image/jpeg', 0.7).split(',')[1];
        resolve(base64);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

const TradeCommentsModal = ({ orderId, visible, onClose, onSaved,originalOrderId }) => {
  const getTodoCount = () => comments.filter(c => c.toDo && !c.todo_completed).length;
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [commentText, setCommentText] = useState('');
  const [files, setFiles] = useState([]);
  const [toDo, setToDo] = useState(false);
  const [referenceLink, setReferenceLink] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [lightboxImg, setLightboxImg] = useState(null);
  
  const handleMarkAsDone = async (commentId) => {
    if (window.confirm('Are you sure you want to mark this todo as done?')) {
      try {
        const res = await fetch(`${BASE_URL}/api/trade-comments/${commentId}/complete-todo`, {
          method: 'PATCH',
          credentials: 'include'
        });
        const data = await res.json();
        if (data.ok) {
          setComments(prev => {
            const newComments = prev.map(c => 
              c._id === commentId ? { ...c, todo_completed: true } : c
            );
            if (onSaved) onSaved({
              type: 'complete',
              todoCount: newComments.filter(c => c.toDo && !c.todo_completed).length
            });
            return newComments;
          });
        } else {
          message.error('Failed to mark todo as done');
        }
      } catch (err) {
        message.error('Failed to mark todo as done');
      }
    }
  };

  useEffect(() => {
    if (visible) fetchComments();
    // eslint-disable-next-line
  }, [visible, orderId]);

  const fetchComments = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BASE_URL}/api/trade-comments/${orderId}`, { credentials: 'include' });
      const data = await res.json();
      setComments(data);
    } catch (err) {
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = ({ fileList }) => {
    setFiles(fileList.map(f => f.originFileObj).filter(Boolean));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitMessage('');
    setError('');
    try {
      if (!commentText.trim()) {
        setError('Comment is required');
        setIsSubmitting(false);
        return;
      }
      if (files.length > 8) {
        setError('Max 8 images allowed');
        setIsSubmitting(false);
        return;
      }
      // Compress images
      let images = [];
      for (let file of files) {
        const base64 = await compressImage(file);
        if ((base64.length * 3 / 4) > 1.2 * 1024 * 1024) {
          setError('Each image must be <= 1.2MB');
          setIsSubmitting(false);
          return;
        }
        images.push(base64);
      }
        const body = { 
        id: orderId, 
        comment: commentText.trim(), 
        images, 
        toDo, 
        reference_link: referenceLink.trim(),
        originalOrderId: originalOrderId || ""
      };
      const res = await fetch(`${BASE_URL}/api/trade-comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.ok) {
        setSubmitMessage('Comment added!');
        setCommentText('');
        setFiles([]);
        setToDo(false);
        setReferenceLink('');
        const newComment = {
          _id: data._id,
          id: orderId,

          comment: body.comment,
          images,
          createdAt: data.createdAt,
          username: data.username,
          toDo: body.toDo,
          originalOrderId: body.originalOrderId || "",
          reference_link: data.reference_link || body.reference_link || '',
        };
        setComments(prev => [newComment, ...prev]);
        if (onSaved) onSaved({ 
          type: 'add',
          todoCount: getTodoCount() + (body.toDo ? 1 : 0)
        });
      } else {
        setError(data.error || 'Failed to add comment');
      }
    } catch (err) {
      setError('Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={visible}
      title="Trade Comments"
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
      style={{ top: 20 }}
    >
      {/* Input, upload, submit at top */}
      <div style={{ marginBottom: 16 }}>
        <Input.TextArea
          rows={3}
          value={commentText}
          onChange={e => setCommentText(e.target.value)}
          maxLength={5000}
          placeholder="Add a comment..."
        />
        {/* Reference Link input above TODO */}
        <Input
          style={{ margin: '8px 0 0 0' }}
          value={referenceLink}
          onChange={e => setReferenceLink(e.target.value)}
          maxLength={500}
          placeholder="Reference link"
        />
        <div style={{ margin: '8px 0 0 0', display: 'flex', alignItems: 'center', gap: 10 }}>
          <input
            type="checkbox"
            id="todo-checkbox"
            checked={toDo}
            onChange={e => setToDo(e.target.checked)}
            style={{ marginRight: 6 }}
          />
          <label htmlFor="todo-checkbox" style={{ fontWeight: 500, fontSize: 15, color: '#1d4ed8', cursor: 'pointer' }}>TODO</label>
        </div>
        <Upload
          fileList={files.map((file, i) => ({ uid: i, name: file.name, status: 'done', originFileObj: file }))}
          beforeUpload={() => false}
          onChange={handleFileChange}
          multiple
          accept="image/*"
          listType="picture"
          maxCount={8}
        >
          <Button style={{ marginTop: 8 }}>Upload Images</Button>
        </Upload>
        <Button
          type="primary"
          style={{ marginTop: 12, background: 'black', color: 'white' }}
          loading={isSubmitting}
          onClick={handleSubmit}
          block
        >
          Submit Comment
        </Button>
        {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
        {submitMessage && <div style={{ color: 'green', marginTop: 8 }}>{submitMessage}</div>}
      </div>
      {/* Comments list scrollable */}
      {loading ? <Spin /> : (
        <div style={{ maxHeight: 'calc(80vh - 250px)', overflowY: 'auto', paddingRight: 4 }}>
          {comments.length === 0 && <div style={{ color: '#888', marginBottom: 12 }}>No comments yet.</div>}
          {comments.map(c => {
            const isToDo = c.toDo === true && !c.todo_completed;
            return (
              <div
                key={c._id}
                style={{
                  marginBottom: 12,
                  padding: 12,
                  border: isToDo ? '2px solid black' : '1px solid #eee',
                  borderRadius: 7,
                  position: 'relative'
                }}
              >
                
                <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {c.comment}
                </div>
                {/* Reference link above username */}
                {c.reference_link && c.reference_link.trim() && (
                  <div style={{ marginBottom: 4 }}>
                    <a
                      href={c.reference_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1d4ed8', textDecoration: 'underline', fontWeight: 500 }}
                    >
                      {c.reference_link}
                    </a>
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>
                  <span style={{ fontWeight: 600, color: '#222', marginRight: 8 }}>{c.username || 'User'}</span>
                  {new Date(c.createdAt).toLocaleString()}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  {c.images && c.images.map((img, i) => (
                    <img
                      key={i}
                      src={`data:image/jpeg;base64,${img}`}
                      alt="thumb"
                      style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, cursor: 'pointer', border: '1px solid #ccc' }}
                      onClick={() => setLightboxImg(img)}
                    />
                  ))}
                </div>
                {c.toDo && !c.todo_completed && (
                  <div style={{ position: 'absolute', bottom: 12, right: 12 }}>
                    <Button 
                      size="small"
                      onClick={() => handleMarkAsDone(c._id)}
                      style={{ 
                        background: '#000',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        padding: '4px 12px'
                      }}
                    >
                      Mark as Done
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Lightbox for image preview */}
      <Modal
        open={!!lightboxImg}
        onCancel={() => setLightboxImg(null)}
        footer={null}
        width={600}
        centered
      >
        {lightboxImg && (
          <img
            src={`data:image/jpeg;base64,${lightboxImg}`}
            alt="full"
            style={{ maxWidth: '100%', maxHeight: 500, display: 'block', margin: '0 auto' }}
          />
        )}
      </Modal>

      {/* Orders Table */}
      <div style={{ marginTop: 20, borderTop: '1px solid #eee', paddingTop: 20 }}>
        <h3>Order Details</h3>
        <OrdersTable 
          orderID={orderId}
         
        />
      </div>
    </Modal>
  );
};

export default TradeCommentsModal;
